// All available skills in the game
export const SKILL_DEFINITIONS = [
    // Combat - fighting and defense
    { id: 'melee', name: 'Melee', category: 'combat' },
    { id: 'shooting', name: 'Shooting', category: 'combat' },
    { id: 'toughness', name: 'Toughness', category: 'combat' },
    // Gathering - resource collection
    { id: 'mining', name: 'Mining', category: 'gathering' },
    { id: 'woodcutting', name: 'Woodcutting', category: 'gathering' },
    { id: 'farming', name: 'Farming', category: 'gathering' },
    { id: 'animals', name: 'Animals', category: 'gathering' },
    { id: 'fishing', name: 'Fishing', category: 'gathering' },
    // Craft - creating things
    { id: 'metalwork', name: 'Metalwork', category: 'craft' },
    { id: 'textiles', name: 'Textiles', category: 'craft' },
    { id: 'mechanics', name: 'Mechanics', category: 'craft' },
    { id: 'construction', name: 'Construction', category: 'craft' },
    { id: 'cooking', name: 'Cooking', category: 'craft' },
    // Knowledge - learning and applying
    { id: 'medicine', name: 'Medicine', category: 'knowledge' },
    { id: 'science', name: 'Science', category: 'knowledge' },
    { id: 'research', name: 'Research', category: 'knowledge' },
    { id: 'art', name: 'Art', category: 'knowledge' },
    // Social - interaction
    { id: 'trading', name: 'Trading', category: 'social' },
    { id: 'leadership', name: 'Leadership', category: 'social' },
    { id: 'teaching', name: 'Teaching', category: 'social' },
];
// Generate random skills for a new pawn
export const generateRandomSkills = ({ rng }) => {
    return SKILL_DEFINITIONS.map((def) => {
        // Random level 0-10 with bias towards lower levels
        const levelRoll = rng();
        const level = Math.floor(levelRoll * levelRoll * 11); // Squared for bias
        // Random aptitude: 70% none, 20% interested, 10% passionate
        const aptitudeRoll = rng();
        const aptitude = aptitudeRoll < 0.7 ? 0 : aptitudeRoll < 0.9 ? 1 : 2;
        return {
            id: def.id,
            name: def.name,
            category: def.category,
            level,
            aptitude,
        };
    });
};
// ============================================================================
// Skill XP System
// ============================================================================
// Maximum skill level
export const MAX_SKILL_LEVEL = 100;
// Early-game thresholds (levels 1-20) are scaled down for colony-sim pacing.
export const SKILL_EARLY_XP_SCALE = 0.08;
// Levels 21+ follow a gentler exponential curve from level 20.
export const SKILL_POST_20_GROWTH = 1.055;
// Baseline cumulative thresholds used before early-game scaling.
const BASE_SKILL_XP_THRESHOLDS = [
    0, // Level 0
    100, // Level 1
    250, // Level 2
    500, // Level 3
    1000, // Level 4
    2000, // Level 5
    4000, // Level 6
    8000, // Level 7
    16000, // Level 8
    32000, // Level 9
    50000, // Level 10
    75000, // Level 11
    100000, // Level 12
    150000, // Level 13
    200000, // Level 14
    300000, // Level 15
    400000, // Level 16
    500000, // Level 17
    650000, // Level 18
    800000, // Level 19
    1000000, // Level 20
];
const buildSkillXpThresholds = () => {
    const thresholds = [0];
    // Levels 1-20: scaled baseline with monotonic clamp.
    for (let level = 1; level <= 20; level++) {
        const baseThreshold = BASE_SKILL_XP_THRESHOLDS[level] ?? 0;
        const scaledThreshold = Math.max(1, Math.floor(baseThreshold * SKILL_EARLY_XP_SCALE));
        const previous = thresholds[level - 1] ?? 0;
        thresholds[level] = Math.max(previous + 1, scaledThreshold);
    }
    // Levels 21+: exponential growth from level 20 with monotonic clamp.
    const level20Threshold = thresholds[20] ?? 0;
    for (let level = 21; level <= MAX_SKILL_LEVEL; level++) {
        const exponentialThreshold = Math.floor(level20Threshold * SKILL_POST_20_GROWTH ** (level - 20));
        const previous = thresholds[level - 1] ?? 0;
        thresholds[level] = Math.max(previous + 1, exponentialThreshold);
    }
    return thresholds;
};
// XP required to reach each level (cumulative)
// Generated to keep difficulty tuning in one place.
export const SKILL_XP_THRESHOLDS = buildSkillXpThresholds();
/**
 * Get the total XP required to reach a specific level
 */
export const getXpForLevel = ({ level }) => {
    if (level <= 0) {
        return 0;
    }
    const maxLevelThreshold = SKILL_XP_THRESHOLDS[MAX_SKILL_LEVEL] ?? 0;
    if (level >= MAX_SKILL_LEVEL) {
        return maxLevelThreshold;
    }
    return SKILL_XP_THRESHOLDS[level] ?? maxLevelThreshold;
};
/**
 * Calculate level from total accumulated XP
 */
export const calculateLevelFromXp = ({ totalXp }) => {
    if (totalXp <= 0) {
        return 0;
    }
    // Binary search for efficiency
    let low = 0;
    let high = SKILL_XP_THRESHOLDS.length - 1;
    while (low < high) {
        const mid = Math.floor((low + high + 1) / 2);
        const threshold = SKILL_XP_THRESHOLDS[mid];
        if (threshold !== undefined && totalXp >= threshold) {
            low = mid;
        }
        else {
            high = mid - 1;
        }
    }
    return Math.min(low, MAX_SKILL_LEVEL);
};
/**
 * Get skill progress information from total XP
 */
export const getSkillProgress = ({ totalXp }) => {
    const level = calculateLevelFromXp({ totalXp });
    const currentLevelXp = getXpForLevel({ level });
    const nextLevelXp = getXpForLevel({ level: level + 1 });
    return {
        level,
        currentXp: totalXp - currentLevelXp,
        xpToNextLevel: nextLevelXp - currentLevelXp,
        totalXp,
    };
};
/**
 * Calculate XP multiplier based on aptitude
 * Passionate pawns learn 50% faster, interested pawns 25% faster
 */
export const getAptitudeXpMultiplier = ({ aptitude }) => {
    switch (aptitude) {
        case 2:
            return 1.5; // Passionate
        case 1:
            return 1.25; // Interested
        default:
            return 1.0; // None
    }
};
//# sourceMappingURL=index.js.map